import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth, API, apiFetch } from '../App';
import { Button } from '../components/ui/button';
import { ShareButtons } from '../components/ShareButtons';
import { toast } from 'sonner';
import { 
  ArrowLeft, Video, Zap, Download, RefreshCw, 
  Clock, CheckCircle, AlertCircle, Loader2
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_gift-melody-2/artifacts/vgbmb5gp_Logo-Wrapped%20Music1.png";
const BANNER_PLAIN = "/assets/brand/logo-inline.png";

const LogoNav = () => (
  <div className="flex items-center">
    <img src={BANNER_PLAIN} alt="Wrapped In Music" className="h-8 w-auto" />
  </div>
);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VideoDetail() {
  const { videoId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [videoMeta, setVideoMeta] = useState({ duration: null, resolution: null });

  // Download video — fetch as blob and trigger save dialog directly.
  // Using window.open() with Content-Disposition headers was unreliable across
  // browsers (Chrome/Safari often opened in a new tab instead of downloading).
  // Fetching as blob + <a download> guarantees the save dialog appears.
  const handleDownload = async () => {
    if (!video?.video_id) return;

    const downloadUrl = `${BACKEND_URL}/api/videos/${video.video_id}/download`;
    const safeTitle = (video.title || video.name || 'wim-video')
      .replace(/[^a-zA-Z0-9 _-]/g, '')
      .trim()
      .replace(/\s+/g, '_')
      .slice(0, 50) || 'wim-video';

    let toastId;
    try {
      toastId = toast.loading('Preparing download...');
      const resp = await fetch(downloadUrl, { credentials: 'include' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${safeTitle}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Give the browser a moment to capture the blob before revoking
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      toast.success('Download started!', { id: toastId });
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Download failed — please try again', { id: toastId });
    }
  };

  const fetchVideo = useCallback(async (checkStatus = false) => {
    try {
      const endpoint = checkStatus 
        ? `/videos/${videoId}/status` 
        : `/videos/${videoId}`;
      
      const { data } = await apiFetch(endpoint);
      setVideo(data);
      return data;
    } catch (error) {
      // v2.9.59 — Same protection as SongDetail. During the long Atlas
      // Cloud / Kling render (sometimes 5-10 min for 30s clips), any
      // transient 5xx / network blip from /videos/{id}/status used to
      // bounce the user out to /dashboard. Now:
      //   - 401 → silent retry (poll will catch up next tick)
      //   - 404 → genuine "video doesn't exist", navigate
      //   - everything else → log + stay on page (toast suppressed
      //     because long renders hit the poll many times)
      const status = error?.response?.status;
      if (status === 401) {
        setLoading(false);
        setCheckingStatus(false);
        return; // poll retries silently
      }
      if (status === 404) {
        toast.error('Video not found');
        navigate('/dashboard');
        setLoading(false);
        setCheckingStatus(false);
        return;
      }
      console.error('Error fetching video (transient, will retry):', status, error?.message);
    } finally {
      setLoading(false);
      setCheckingStatus(false);
    }
  }, [videoId, navigate]);

  useEffect(() => {
    fetchVideo();
  }, [fetchVideo]);

  const handleCheckStatus = async () => {
    setCheckingStatus(true);
    toast.info('Checking video generation status... This may take a few minutes.');
    await fetchVideo(true);
  };

  const handleRefresh = async () => {
    setPolling(true);
    await fetchVideo();
    setPolling(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'processing':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'failed':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-yellow-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            {[0, 0.1, 0.2, 0.3, 0.4].map((delay) => (
              <div key={`bar-${delay}`} className="audio-bar" style={{ animationDelay: `${delay}s` }}></div>
            ))}
          </div>
          <span className="text-white">Loading video...</span>
        </div>
      </div>
    );
  }

  if (!video) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 glass">
        <div className="container-wide flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/dashboard')}
              className="text-zinc-400 hover:text-white transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <LogoNav />
          </div>
          <div className="credit-badge">
            <Zap className="h-4 w-4" />
            {user?.credits || 0} credits
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 container-narrow" data-testid="video-detail">
        {/* Video Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            {getStatusIcon(video.status)}
            <span className="text-sm text-zinc-400 capitalize">{video.status}</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{video.title}</h1>
          <p className="text-zinc-400 mb-4">{video.prompt}</p>
          
          <div className="flex flex-wrap gap-2 mb-6">
            <span className="tag">{video.duration}s</span>
            <span className="tag">{video.size}</span>
          </div>
        </div>

        {/* Video Player */}
        {video.status === 'completed' && video.video_url && (
          <div className="mb-8">
            <div className="video-container">
              <video 
                src={video.video_url.startsWith('http') ? video.video_url : `${BACKEND_URL}${video.video_url}`}
                controls
                className="w-full h-full"
                data-testid="video-player"
                onLoadedMetadata={(e) => {
                  const v = e.target;
                  const mins = Math.floor(v.duration / 60);
                  const secs = Math.round(v.duration % 60);
                  setVideoMeta({
                    duration: `${mins}:${String(secs).padStart(2, '0')}`,
                    resolution: `${v.videoWidth}x${v.videoHeight}`
                  });
                }}
              />
            </div>
            <div className="flex gap-4 mt-4">
              <ShareButtons 
                type={video.video_type || 'video'} 
                id={video.video_id} 
                title={video.title || video.name} 
                videoUrl={video.video_url}
              />
              <Button
                onClick={handleDownload}
                className="btn-primary px-6 py-3 rounded-full inline-flex items-center"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Video
              </Button>
            </div>
          </div>
        )}

        {/* Processing State */}
        {video.status === 'processing' && (
          <div className="glass-card p-8 text-center">
            {checkingStatus ? (
              <>
                <Loader2 className="h-12 w-12 text-primary mx-auto mb-4 animate-spin" />
                <h3 className="text-xl font-bold text-white mb-2">Generating Your Video</h3>
                <p className="text-zinc-400 mb-4">
                  Sora 2 is creating your video. This can take 2-5 minutes depending on duration and resolution.
                </p>
                <p className="text-sm text-zinc-500">
                  Please wait... The page will update when complete.
                </p>
              </>
            ) : (
              <>
                <div className="flex justify-center mb-4">
                  <div className="flex gap-1">
                    {[0, 0.1, 0.2, 0.3, 0.4].map((delay) => (
                      <div key={`queued-bar-${delay}`} className="audio-bar" style={{ animationDelay: `${delay}s` }}></div>
                    ))}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Video Queued</h3>
                <p className="text-zinc-400 mb-6">
                  Your video is queued for generation. Click below to start the generation process.
                </p>
                <div className="flex justify-center gap-4">
                  <Button
                    onClick={handleCheckStatus}
                    className="btn-primary"
                    data-testid="start-generation-btn"
                  >
                    <Video className="h-4 w-4 mr-2" />
                    Start Generation
                  </Button>
                  <Button
                    onClick={handleRefresh}
                    disabled={polling}
                    className="btn-secondary"
                    data-testid="refresh-btn"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${polling ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Failed State */}
        {video.status === 'failed' && (
          <div className="glass-card p-8 text-center border-red-500/20">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Generation Failed</h3>
            <p className="text-zinc-400 mb-4">
              Something went wrong while generating your video. Please try again.
            </p>
            <Button onClick={() => navigate('/create-video')} className="btn-primary">
              Try Again
            </Button>
          </div>
        )}

        {/* Details */}
        <div className="glass-card p-6 mt-8">
          <h3 className="text-lg font-bold text-white mb-4">Generation Details</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-400">Duration</span>
              <span className="text-white">{videoMeta.duration || '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Resolution</span>
              <span className="text-white">{videoMeta.resolution || '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400">Created</span>
              <span className="text-white">{new Date(video.created_at).toLocaleString()}</span>
            </div>
            {video.song_id && (
              <div className="flex justify-between">
                <span className="text-zinc-400">Linked Song</span>
                <button 
                  onClick={() => navigate(`/song/${video.song_id}`)}
                  className="text-primary hover:underline"
                >
                  View Song
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
